import {DocumentStoreApi} from '@/components/document/store';
import {STATE_VALUE_WILDCARD_SUFFIX} from '@/constants/engine';
import {DomainValueReference, StateReference, ValueDefinition} from '@/model/types';
import {ValueResolver, ValueResolverOptions, ValueType} from '../types';
import {getReferenceType, isReferenceAlwaysVisible, isFieldVisible} from '../utils/reference';
import {applyDomainTransform, lookupValueDomain, mapValueDomainList} from './domain';

export const resolveReference = <T>(
  reference: DomainValueReference | StateReference,
  store: DocumentStoreApi,
  values: ValueDefinition[],
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
) => {
  const {data, locale} = store.getState();
  const type = getReferenceType(reference.$ref);
  const {transform = input => input as T} = options;

  switch (type) {
    case 'domainValue': {
      if (options.keepReference || options.keepDomainReference) {
        return reference as T;
      }
      return resolver(lookupValueDomain(values, reference, resolver, options, locale) as ValueType, options);
    }

    case 'domainValueList':
      return mapValueDomainList(values, reference, resolver).map(ref => {
        if ('$ref' in ref) {
          return resolver(ref, options);
        }
        return ref as T;
      }) as T;

    case 'state': {
      const isVisible =
        isReferenceAlwaysVisible(reference.$ref) || isFieldVisible(reference.$ref, options.visibilitySet);
      const value = isVisible ? data[reference.$ref] : undefined;
      if (value === undefined)
        return transform(reference.transform ? applyDomainTransform(undefined, reference.transform) : '');
      if (typeof value === 'string') return transform(value);
      if (options.keepReference) return value as T;
      if ('$ref' in value) return resolver({...reference, ...value}, options);
      return resolver(value, options);
    }

    case 'stateQuery': {
      return transform(
        Object.entries(data)
          .filter(([key]) => key.startsWith(reference.$ref.slice(0, -STATE_VALUE_WILDCARD_SUFFIX.length)))
          .filter(([key]) => isReferenceAlwaysVisible(key) || isFieldVisible(key, options.visibilitySet))
          .map(([key, value]) => {
            if (options.keepReference || options.keepDomainReference) {
              return key;
            } else {
              return resolver(value, options) as string;
            }
          }),
      );
    }
  }
};
